import { Card } from '@/components/ui';
import { useBrandDashboardStats } from '@/hooks/queries/useBrandDashboardStats';
import { Tabs, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversionChart } from './ConversionChart';
import { DashboardMetricsCards } from './DashboardMetricsCards';
import { DashboardStatsError } from './DashboardStatsError';
import { RevenueCommissionChart } from './RevenueCommissionChart';

interface DashboardStatsGridProps {
  /** Optional column rendered beside the stat cards (see `DashboardMetricsCards`). */
  aside?: ReactNode;
}

/**
 * Main dashboard stats grid component - orchestrates data fetching and layout
 */
export const DashboardStatsGrid: React.FC<DashboardStatsGridProps> = ({ aside }) => {
  const { t } = useTranslation();
  const { data: stats, isLoading, error, refetch } = useBrandDashboardStats();

  // The stats could not be read: say so in the cards' place, and keep the aside
  // (the to-do card reads its own figures and does not depend on them).
  if (error) {
    return (
      <DashboardMetricsCards
        statsError={<DashboardStatsError error={error} onRetry={refetch} />}
        aside={aside}
      />
    );
  }

  return (
    <VStack gap={4} alignItems="stretch" width="100%">
      {/* Top Cards: Financial Metrics & Performance */}
      <DashboardMetricsCards
        attributedRevenue={stats?.attributedRevenue}
        totalCommissions={stats?.totalCommissions}
        aov={stats?.aov}
        conversions={stats?.conversions}
        roiAmount={stats?.roiAmount}
        cpa={stats?.cpa}
        chartData={stats?.chartData}
        conversionChartData={stats?.conversionChartData}
        isLoading={isLoading}
        aside={aside}
      />

      {/* Chart Section with Tabs */}
      <Card.Root>
        <Tabs.Root defaultValue="revenue" colorPalette="primary">
          <Card.Header>
            <Tabs.List>
              <Tabs.Trigger value="revenue" fontSize="sm">
                {t('dashboard.stats.tabs.revenueCommissions')}
              </Tabs.Trigger>
              <Tabs.Trigger value="conversions" fontSize="sm">
                {t('dashboard.stats.tabs.conversions')}
              </Tabs.Trigger>
            </Tabs.List>
          </Card.Header>
          <Card.Body>
            <Tabs.Content value="revenue">
              <RevenueCommissionChart
                data={stats?.chartData}
                currency={stats?.attributedRevenue?.currency}
                isLoading={isLoading}
              />
            </Tabs.Content>
            <Tabs.Content value="conversions">
              <ConversionChart data={stats?.conversionChartData} isLoading={isLoading} />
            </Tabs.Content>
          </Card.Body>
        </Tabs.Root>
      </Card.Root>
    </VStack>
  );
};
